// src/components/quizzes/kit/Faller.js
//
// One falling thing in a bonus round (a diamond, a bubble, a bomb…).
//
// WHY THIS EXISTS (Nov 2026): on a phone, tapping quickly made every
// falling diamond stop for a moment. The fall used to be a framer-motion
// animation, which moves each item from JavaScript on every frame — so
// whenever a tap was busy (scoring, the sound, the pop) every item froze
// until it finished. Fast tapping = lots of small freezes.
//
// Here the fall is a Web Animations API animation of `transform`, which the
// browser runs on its own compositor thread. It keeps moving smoothly no
// matter what the page is doing, and taps still land on where the item
// really is.
//
// Props
//   item      passed back to onTap / onDone
//   speed     seconds to fall the whole way
//   drift     px sideways over the fall
//   spin      degrees turned over the fall
//   distance  how far to fall (CSS length, default 112vh)
//   paused    true = hold still (the End game question is up)
//   onTap(item, event)   pointer down on it
//   onDone(item)         it fell off the bottom
//
// It only re-draws when `item`, `className` or `paused` change, so a tap elsewhere never touches
// the items already falling. onTap / onDone must be stable functions (the
// games pass a useCallback that reads the latest state from a ref).

import React, { memo, useEffect, useRef } from "react";

const Faller = ({ item, speed, drift = 0, spin = 0, distance = "112vh", paused = false, onTap, onDone, className = "", style, children, ...rest }) => {
  const ref = useRef(null);
  const anim = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const to = `translate3d(${drift}px, ${distance}, 0) rotate(${spin}deg)`;
    if (typeof el.animate === "function") {
      const a = el.animate([{ transform: "translate3d(0, 0, 0) rotate(0deg)" }, { transform: to }], {
        duration: Math.max(300, speed * 1000),
        easing: "linear",
        fill: "forwards",
      });
      a.onfinish = () => onDone?.(item);
      anim.current = a;
      return () => {
        a.onfinish = null;
        try {
          a.cancel();
        } catch {
          /* already gone */
        }
      };
    }
    // very old browsers: a CSS transition does the same job
    el.style.transition = `transform ${speed}s linear`;
    const r = requestAnimationFrame(() => {
      el.style.transform = to;
    });
    const t = setTimeout(() => onDone?.(item), speed * 1000);
    return () => {
      cancelAnimationFrame(r);
      clearTimeout(t);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const a = anim.current;
    if (!a) return;
    try {
      if (paused && a.playState === "running") a.pause();
      else if (!paused && a.playState === "paused") a.play();
    } catch {
      /* ignore */
    }
  }, [paused]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...style, willChange: "transform" }}
      onPointerDown={(e) => onTap?.(item, e)}
      {...rest}
    >
      {children}
    </div>
  );
};

// Re-draw only for a new item or a pause change — never because the score
// or the combo changed somewhere else on the screen.
export default memo(Faller, (a, b) => a.item === b.item && a.paused === b.paused && a.className === b.className);
